<script lang="ts" setup>
import type { SelectionModeEnum } from "@lxc/app-device-common";
import { storeToRefs } from "pinia";
import { SearchMode } from "~/core/composables/useSearch";
import { useToggle } from "~/core/composables/useToggle";
import {
  DEFAULT_FIRST_PAGE,
  DEFAULT_PAGE_SIZE,
} from "~/core/constants/constants";
import type { PaginatedList } from "~/core/models/PaginatedList.interface";
import {
  NotificationKey,
  showNotificationWarning,
} from "~/core/utils/notifications";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import { useAttachDetachEntitiesToSector } from "~/modules/sector/composables/useAttachDetachEntitiesToSector";
import type { Sector } from "~/modules/sector/models/Sector.interface";
import type { SectorAttachDetachPayload } from "~/modules/sector/models/SectorAttachDetachPayload.interface";
import type { SelectedItemType } from "~/modules/sector/models/SelectedItem.type";
import { displayActionStatus } from "~/modules/sector/utils/displayActionsStatus";
import { getSearchComposable } from "~/modules/sector/utils/getSearchComposable";
import type { GroupsUserV2 } from "~/modules/user/models/GroupsUserV2.interface";
import type { User } from "~/modules/user/models/User.interface";
import type { SectorResource } from "~/types";
import ILxcTrash2 from "~icons/lxc/trash-2";

const props = defineProps<{
  primaryKey: string;
  isLoading: boolean;
  selection?: boolean;
  items: PaginatedList<SelectedItemType[]> | null;
  displayActions?: boolean;
  modelValue: SelectedItemType[];
  resource: SectorResource;
  rowClickSelectionMode?: SelectionModeEnum;
  getDescription: (item: SelectedItemType) => string;
  selectionMessage?: string;
  sector: Sector;
  minWidth?: string;
}>();

const emit = defineEmits([
  "pagination:getNextPage",
  "update:modelValue",
  "rowClick",
  "actions:detach",
]);

const { currentOrganization } = storeToRefs(useOrganizationStore());
const { t } = useI18n();
const [detachModalVisible, toggleDetachModalVisible] = useToggle();
const useSearch = getSearchComposable(props.resource);
const { itemType, getDescription, getItemIds } = useSearch!({
  organizationCode: currentOrganization.value?.code,
  sectorCode: props.sector.code,
  outSector: true,
  searchMode: SearchMode.FILTER_SEARCH,
});
const {
  error: errorAttachDetach,
  results: resultsAttachDetach,
  attachDetachEntities,
} = useAttachDetachEntitiesToSector(currentOrganization.value?.code);

const detailTable = ref();
const rowSelection: Ref<SelectedItemType[]> = ref([]);
const quickActionButtonConfig = ref({
  icon: ILxcTrash2,
  title: t("sectors.details.buttons.detach"),
  event: () => {
    toggleDetachModalVisible();
  },
});

const canDetach: ComputedRef<boolean> = computed(() => {
  return !props.sector.isRootSector;
});
watch(
  () => props.items,
  async () => {
    await nextTick();
    refreshTableSelection();
  },
);

watch(
  () => props.modelValue,
  async (value) => {
    rowSelection.value = value;
    refreshTableSelection();
  },
);

/**
 * When selecting a specific row
 * @param selection
 */
function onSelectRow(selection: SelectedItemType[]) {
  const temporarySet = new Set<SelectedItemType>();

  rowSelection.value
    .filter(
      (selected) =>
        !detailTable.value.data.find((row: SelectedItemType) => {
          return row[props.primaryKey] === selected[props.primaryKey];
        }),
    )
    .forEach((value) => temporarySet.add(value));
  selection.forEach((value) => temporarySet.add(value));
  rowSelection.value = Array.from(temporarySet.values());

  updateModel();
}

function updateModel() {
  emit("update:modelValue", rowSelection.value);
}

function refreshTableSelection() {
  detailTable.value.toggleTableSelectionMode(rowSelection.value.length > 0);
  detailTable.value.data.forEach((row: Dtwin | User | GroupsUserV2) => {
    const selected = rowSelection.value.some((item: SelectedItemType) => {
      return row[props.primaryKey] === item[props.primaryKey];
    });
    detailTable.value.toggleRowSelection(row, selected);
  });
}

async function getNextByPageAndSize(
  page: number = DEFAULT_FIRST_PAGE,
  pageSize: number = DEFAULT_PAGE_SIZE,
) {
  emit("pagination:getNextPage", page, pageSize);
}

function clearSelectionAndCloseDetachModal() {
  closeDetachModal();
  clearSelection();
}

function closeDetachModal() {
  toggleDetachModalVisible(false);
}

function clearSelection() {
  rowSelection.value = [];
  refreshTableSelection();
}

/**
 * Detach item(s) from the sector
 */
async function detachFromSector() {
  if (rowSelection.value.length > 0) {
    const detachEntitiesPayload: SectorAttachDetachPayload = {
      assign: [],
      unassign: getItemIds(rowSelection.value),
    };
    await attachDetachEntities(
      props.sector.code,
      props.resource,
      detachEntitiesPayload,
    );
    if (errorAttachDetach.value) {
      errorAttachDetach.value.notify(NotificationKey.error);
    } else {
      const responseWithItemDescription = resultsAttachDetach.value.map(
        (action) => ({
          ...action,
          name: getDescription(
            rowSelection.value.find(
              (item) => item[props.primaryKey] === action.name,
            ),
          ),
        }),
      );
      displayActionStatus(
        responseWithItemDescription,
        `${NotificationKey.detachSuccess}.${itemType}`,
        `${NotificationKey.detachError}.${itemType}`,
        props.sector,
        t,
      );
    }

    clearSelectionAndCloseDetachModal();
    emit("actions:detach");
  } else {
    showNotificationWarning(
      t("sectors.modals.confirmDetach.errors.list_empty"),
    );
  }
}
</script>

<template>
  <quick-actions-toolbar
    v-if="rowSelection.length > 0 && canDetach && displayActions"
    :quick-action-button-config="quickActionButtonConfig"
    :selection-message="
      props.selectionMessage
        ? t(props.selectionMessage, rowSelection.length)
        : ''
    "
    @cancel-selection="clearSelection"
  />

  <lxc-table
    ref="detailTable"
    class="mb-4"
    :is-loading="isLoading"
    :data="items?.results"
    :context="items?.pagination"
    :empty-text="t(`${resource}.empty`)"
    :min-width="minWidth"
    clickable
    :row-click-selection-mode="rowClickSelectionMode"
    @select="onSelectRow"
    @row-click="$emit('rowClick', $event)"
    @change-page-and-page-size="getNextByPageAndSize"
  >
    <lxc-table-column v-if="selection" type="selection" />
    <slot />
  </lxc-table>

  <lxc-common-modal
    :show="detachModalVisible"
    class="!min-w-[40%] !max-w-4xl"
    body-class="!max-w-4xl"
    @close="closeDetachModal"
  >
    <template #header>
      <h3>{{ t(`sectors.modals.confirmDetach.${resource}.title`) }}</h3>
    </template>

    <template #body>
      <div>
        <information-row
          :title="
            t(
              `sectors.modals.confirmDetach.${props.resource}.warning`,
              rowSelection.length,
            )
          "
        />
        <p class="update-description">
          {{
            t(
              `sectors.modals.confirmDetach.${props.resource}.description`,
              {
                sectorLabel: sector.name,
                name: getDescription(rowSelection[0]),
              },
              rowSelection.length,
            )
          }}
        </p>
        <ul v-if="rowSelection.length > 1">
          <li v-for="item in rowSelection" :key="item">
            {{ getDescription(item) }}
          </li>
        </ul>
      </div>
    </template>

    <template #footer>
      <div class="flex grow shrink-0 justify-end gap-4">
        <lxc-button
          type="secondary"
          html-type="button"
          :title="t('button.cancel')"
          @click="closeDetachModal"
        >
          {{ t("button.cancel") }}
        </lxc-button>

        <lxc-button
          type="primary"
          html-type="button"
          :title="t('button.confirm')"
          @click="detachFromSector"
        >
          {{ t("button.confirm") }}
        </lxc-button>
      </div>
    </template>
  </lxc-common-modal>
</template>
